import React from 'react';
import styled from '@emotion/styled';
import { Table, TableProps } from '@applyboard/crystal-ui';

export type ColumnWidthConfig = {
  [columnKey: string]: string | number;
};

export type StyledTableProps<T> = TableProps<T> & {
  columnWidths?: ColumnWidthConfig;
};

export function StyledTable<T>(props: StyledTableProps<T>) {
  const { columnWidths, ...tableProps } = props;

  // Create CSS based on column accessorKey mapping
  const generateColumnStyles = () => {
    if (!columnWidths) return '';

    return Object.entries(columnWidths)
      .map(([accessorKey, width]) => {
        const columnIndex = props.columns.findIndex((col) => col.accessorKey === accessorKey);
        if (columnIndex === -1) return '';

        const widthValue = typeof width === 'number' ? `${width}px` : width;

        return `
          /* Target column ${columnIndex + 1} (${accessorKey}) */
          & table > thead > tr > th:nth-of-type(${columnIndex + 1}) {
            width: ${widthValue};
            min-width: ${widthValue};
            max-width: ${widthValue};
          }

          & table > tbody > tr > td:nth-of-type(${columnIndex + 1}) {
            width: ${widthValue};
            min-width: ${widthValue};
            max-width: ${widthValue};
          }
        `;
      })
      .join('');
  };

  return (
    <TableWrapper customStyles={generateColumnStyles()}>
      <Table {...tableProps} />
    </TableWrapper>
  );
}

const TableWrapper = styled.div<{ customStyles: string }>`
  /* Use automatic table layout so columns resize responsively with content */
  & table {
    table-layout: auto;
    width: 100%;
  }

  /* Header cells: allow wrapping and align all text to the left, with no background color */
  & table > thead > tr > th {
    white-space: normal;
    word-wrap: normal;
    overflow-wrap: normal;
    background-color: transparent;
    text-align: left;
    vertical-align: middle;
  }

  /* Target the inner div within header cells */
  & table > thead > tr > th > div {
    white-space: normal;
    word-wrap: normal;
    overflow-wrap: normal;
    text-align: left;
    display: inline-block;
  }

  /* Remove row borders for a cleaner look (keep only header separator) */
  & table > tbody > tr > td {
    border-top: none;
    border-bottom: none;
  }

  ${({ customStyles }) => customStyles}
`;
